/**
 * Attorney questions — agent-side creation and listing (slice 2, DARK).
 *
 * A created question lands on stage 'queued' and stops there. Nothing routes it, emails an
 * attorney, or answers it yet; the routing engine is slice 3.
 *
 * Authorization is server-side: both tables are deny-by-default and reachable only through
 * these SECURITY DEFINER RPCs, which require an ACTIVE profile in
 * admin/superadmin/manager/support_admin/support_agent. Agents see the questions they raised;
 * management sees all.
 *
 * See docs/DESIGN_ATTORNEY_QUESTION_ROUTING.md
 */

import { supabase } from "./supabase";

export type AttorneyTaskType =
  | "attorney_required"
  | "out_of_state_attorney_required"
  | "coverage_question";

export const ATTORNEY_TASK_TYPES: {
  value: AttorneyTaskType;
  label: string;
  help: string;
  states: "one" | "many" | "none";
}[] = [
  {
    value: "attorney_required",
    label: "Attorney required",
    help: "One state. Goes to that state's attorney.",
    states: "one",
  },
  {
    value: "out_of_state_attorney_required",
    label: "Out-of-state attorney required",
    help: "Pick every state involved. Each state's attorney is asked separately.",
    states: "many",
  },
  {
    value: "coverage_question",
    label: "Coverage question",
    help: "Not routed to an attorney. Goes to the coverage questions mailbox.",
    states: "none",
  },
];

export interface AttorneyQuestionRow {
  support_task_id: string;
  task_number: number;
  task_type: AttorneyTaskType;
  stage: string;
  question_text: string;
  states: string[];
  member_name: string | null;
  membership_id: string | null;
  created_by_name: string | null;
  created_at: string;
  disposition: string | null;
  attorney_notes: string | null;
}

/** Operator-readable stage labels — the raw values are internal vocabulary. */
export const STAGE_LABELS: Record<string, string> = {
  submitted: "Submitted",
  queued: "Waiting to send",
  sent_to_attorney: "With attorney",
  answered: "Answered",
  agent_notified: "Answer ready for you",
  closed: "Closed",
  exception_review_required: "Wrong attorney — under review",
  training_review_required: "Not a legal question — under review",
};

function friendlyError(message: string): string {
  if (message.includes("not_authorized")) {
    return "You do not have permission to raise attorney questions.";
  }
  if (message.includes("no_active_coverage_for_state:")) {
    const state = message
      .split("no_active_coverage_for_state:")[1]
      ?.slice(0, 2);
    return `No attorney is currently assigned to ${state ?? "that state"}. Ask an admin to set coverage before raising this question.`;
  }
  if (message.includes("exactly_one_state_required")) {
    return "Choose exactly one state for an Attorney Required question.";
  }
  if (message.includes("at_least_one_state_required")) {
    return "Choose at least one state.";
  }
  if (message.includes("coverage_question_takes_no_state")) {
    return "Coverage questions are not routed by state — clear the state selection.";
  }
  if (message.includes("question_required")) {
    return "Enter the member's question.";
  }
  if (message.includes("member_required")) {
    return "Choose the member this question is about.";
  }
  if (message.includes("invalid_task_type")) {
    return "Choose a question type.";
  }
  if (message.includes("invalid_state_code")) {
    return "That state code is not valid.";
  }
  return message;
}

export interface CreateAttorneyQuestionInput {
  contact_id: string;
  task_type: AttorneyTaskType;
  question_text: string;
  states: string[];
  priority?: string;
}

export async function createAttorneyQuestion(
  input: CreateAttorneyQuestionInput,
): Promise<{ support_task_id: string; task_number: number }> {
  const { data, error } = await supabase.rpc("create_attorney_question", {
    p_contact_id: input.contact_id,
    p_task_type: input.task_type,
    p_question_text: input.question_text,
    p_states: input.states,
    p_priority: input.priority ?? "medium",
  });
  if (error) {
    throw new Error(friendlyError(error.message));
  }
  // RETURNS TABLE surfaces as an array of one row.
  const row = Array.isArray(data) ? data[0] : data;
  return row as { support_task_id: string; task_number: number };
}

export async function listAttorneyQuestions(
  limit = 100,
): Promise<AttorneyQuestionRow[]> {
  const { data, error } = await supabase.rpc("list_attorney_questions", {
    p_limit: limit,
  });
  if (error) {
    throw new Error(friendlyError(error.message));
  }
  return (data ?? []) as AttorneyQuestionRow[];
}

/**
 * States an agent may choose: only those with an active PRIMARY attorney. The server refuses
 * anything else, so offering the full 50 would just manufacture failed submissions.
 *
 * Deliberately NOT list_attorney_coverage — that one is management-only and returns attorney
 * names and emails. A support agent needs the state codes and nothing more.
 */
export async function listCoveredStates(): Promise<string[]> {
  const { data, error } = await supabase.rpc("list_covered_states");
  if (error) {
    throw new Error(friendlyError(error.message));
  }
  return ((data ?? []) as { state_code: string }[]).map((r) => r.state_code);
}
